import { DOCUMENT, Injectable, Signal, computed, effect, inject, signal } from '@angular/core';

/** Describes a selectable UI theme. */
export interface AppTheme {
  readonly id: string;
  readonly labelKey: string;
  /**
   * Dark mode needs BOTH masta selectors:
   *   - `mel-dark-mode`   → MEL design tokens + masta component CSS
   *   - `masta-dark-mode` → PrimeNG MastaPreset dark theme (set by provideMEL)
   */
  readonly htmlClasses: readonly string[];
}

/**
 * Registry of available themes. Add new entries here to scale to additional themes
 */
export const APP_THEMES: readonly AppTheme[] = [
  { id: 'light', labelKey: 'header.theme.light', htmlClasses: [] },
  { id: 'dark', labelKey: 'header.theme.dark', htmlClasses: ['mel-dark-mode', 'masta-dark-mode'] },
] as const;

const THEME_STORAGE_KEY = 'service-hub.theme';

/**
 * Manages the active UI theme.
 * {@link APP_THEMES} entry.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);

  readonly themes: readonly AppTheme[] = APP_THEMES;

  readonly activeThemeId = signal<string>(this.readInitialThemeId());

  readonly activeTheme: Signal<AppTheme> = computed(
    () => this.themes.find((theme) => theme.id === this.activeThemeId()) ?? this.themes[0],
  );

  constructor() {
    effect(() => {
      const active = this.activeTheme();
      const html = this.document.documentElement;

      for (const theme of this.themes) {
        for (const cssClass of theme.htmlClasses) {
          html.classList.remove(cssClass);
        }
      }
      for (const cssClass of active.htmlClasses) {
        html.classList.add(cssClass);
      }

      this.persist(active.id);
    });
  }

  setTheme(id: string): void {
    if (this.themes.some((theme) => theme.id === id)) {
      this.activeThemeId.set(id);
    }
  }

  private readInitialThemeId(): string {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored && APP_THEMES.some((theme) => theme.id === stored)) {
        return stored;
      }
    } catch {
      // localStorage may be unavailable (e.g. privacy mode) — fall back silently.
    }
    return APP_THEMES[0].id;
  }

  private persist(id: string): void {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, id);
    } catch {
      // Ignore persistence errors.
    }
  }
}
